/**
 * `DD.MM.YYYY` string parsers — the single source for the parse that was copy-pasted
 * across the checkout Zod schemas, the date input, and the DaVinci wire mapper.
 *
 * The two functions are NOT interchangeable:
 *   - `parseGermanDateLocal` → LOCAL midnight, for AGE validation (the calendar date is
 *     what matters; a UTC instant would shift the day in some timezones).
 *   - `parseGermanDateUtc`   → UTC midnight, for the DaVinci wire (the server expects a
 *     UTC instant).
 *
 * Both intentionally keep the prior lenient behaviour (no rollover guard — e.g.
 * `31.02.2024` rolls into March); callers that need strict validity (e.g. BaseDateInput)
 * keep their own roundtrip check. See claude/specs/ChildVsInfant.
 */

/** Parse `DD.MM.YYYY` to a LOCAL `Date` (midnight in the browser timezone). For age checks. */
export function parseGermanDateLocal(date: string): Date {
  const [day, month, year] = date.split('.').map(Number);
  return new Date(year, month - 1, day);
}

/** Parse `DD.MM.YYYY` to a UTC-midnight `Date`, or `null` if malformed. For wire serialization. */
export function parseGermanDateUtc(date: string): Date | null {
  if (!date) return null;

  const parts = date.split('.');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  return new Date(Date.UTC(year, month - 1, day));
}
